import PlusIcon from "@heroicons/react/24/solid/PlusIcon";
import { Button, SvgIcon, Switch, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { EServiceTypesPermissions } from "resources/permissions/permissions.enum";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import usePermission from "shared/helpers/hooks/usePermission";
import useTranslation from "shared/helpers/hooks/useTranslation";
import SharedDialog from "shared/ui/Dialog";
import RightDrawer from "shared/ui/Drawer";
import BasicTable from "shared/ui/Table";
import { ERequestStatus } from "store/enums/index.enum";
import { ICampaignTranslation } from "store/interfaces/campaignTranslation";
import {
  AutoTranslateCampaign,
  DeleteCampaignTranslation,
  GetAutoTranslateLanguageList,
  GetCampaignTranslationGrid,
  GetTranslationLanguages,
  MakeCampaignTranslationVisible,
  selectAutotranslateLanguages,
  selectCampaignTranslations,
} from "store/slicers/campaignTranslations";
import {
  selectLoadingState,
  setLoading,
  setTableLoading,
} from "store/slicers/common";
import AddCampaignTranslation from "./components/AddCampaignTranslation";
import QuestionTranslation from "./components/QuestionTranslation";
import { columns } from "./constants";
import {
  GetCampaignById,
  selectCampaignInfo,
} from "store/slicers/campaignDetail";
import DistributionTextTranslation from "./components/DistributionTextTranslation";

const CampaignTranslations = () => {
  const t = useTranslation();
  const [activeRow, setActiveRow] = useState<ICampaignTranslation>();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const campaignInfo = useSelector(selectCampaignInfo);
  const [isWarningOpen, setWarningOpen] = useState(false);
  const [isQuestionsModalOpen, setQuestionsModalOpen] = useState(false);
  const [isDistModalOpen, setDistModalOpen] = useState(false);
  const dispatch = useAsyncDispatch();
  const isLoading = useSelector(selectLoadingState);
  const params = useParams();

  const hasEditPermission = usePermission(EServiceTypesPermissions.Update);
  const hasCreatePermission = usePermission(EServiceTypesPermissions.Create);
  const hasDeletePermission = usePermission(EServiceTypesPermissions.Delete);

  const data = useSelector(selectCampaignTranslations);
  const autotranslateLanguages = useSelector(selectAutotranslateLanguages);

  const refetchGrid = useCallback(async () => {
    dispatch(setTableLoading(true));
    await dispatch(GetCampaignTranslationGrid(params?.id));
    dispatch(setTableLoading(false));
  }, [dispatch, params?.id]);

  const handleEdit = async (row: ICampaignTranslation) => {
    setActiveRow(row);
    setQuestionsModalOpen(true);
  };

  const handleClose = () => {
    setActiveRow(undefined);
  };

  const onAddSuccess = () => {
    setQuestionsModalOpen(false);
    setDrawerOpen(false);
    refetchGrid();
  };

  const onUpdateSuccess = () => {
    setDrawerOpen(false);
    refetchGrid();
  };

  const onAddEditDistributionSuccess = async () => {
    dispatch(setTableLoading(true));
    await dispatch(GetCampaignById(Number(params?.id)));
    dispatch(setTableLoading(false));
    setDistModalOpen(false);
    toast.success("Successfully saved");
  };

  const handleOpenWarning = (row: ICampaignTranslation) => {
    setActiveRow(row as ICampaignTranslation);
    setWarningOpen(true);
  };

  const handleDelete = async () => {
    if (!activeRow) {
      return;
    }
    dispatch(setLoading(true));
    const { meta } = await dispatch(
      DeleteCampaignTranslation({
        id: Number(params?.id),
        languageID: activeRow.languageID,
      })
    );
    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      dispatch(setLoading(false));
      return;
    }
    setActiveRow(undefined);
    await refetchGrid();
    dispatch(setLoading(false));
    toast.success("Service is deleted");
  };

  const handleTranslate = async (row: ICampaignTranslation) => {
    dispatch(setLoading(true));

    const autotranslateLanguage = autotranslateLanguages.result.find(
      (i) => i.codeAlpha1 === row.languageCode
    );

    const { meta } = await dispatch(
      AutoTranslateCampaign({
        campaignId: Number(params?.id),
        languageCode: autotranslateLanguage.fullCode,
        ToLanguageCodeAlpha: autotranslateLanguage.codeAlpha1,
      })
    );

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      dispatch(setLoading(false));
      return;
    }

    setActiveRow(row);
    dispatch(setLoading(false));
    toast.success("Autotranslation is Completed");
    setQuestionsModalOpen(true);
  };

  const handleDistributionText = (rowData: ICampaignTranslation) => {
    setActiveRow(rowData);
    setDistModalOpen(true);
  };

  const distribution_condition = useMemo(() => {
    const dist_text_default = campaignInfo?.campaignTranslations.find(
      (i) => i.languageID === campaignInfo.defaultLanguageID
    );
    const has_translation = !!(
      dist_text_default?.pushTitle ||
      dist_text_default?.pushContent ||
      dist_text_default?.smsMessage ||
      dist_text_default?.emailSubject ||
      dist_text_default?.emailBody
    );
    return hasEditPermission && has_translation;
  }, [
    campaignInfo?.campaignTranslations,
    campaignInfo?.defaultLanguageID,
    hasEditPermission,
  ]);

  const getActions = (rowData: ICampaignTranslation) => {
    return [
      ...(hasEditPermission && !rowData?.isDefaultLanguage
        ? [
            {
              label: "Edit Details",
              onClick: () => handleEdit(rowData),
            },
          ]
        : []),
      ...(distribution_condition && !rowData?.isDefaultLanguage
        ? [
            {
              label: "Distribution Texts",
              onClick: () => handleDistributionText(rowData),
            },
          ]
        : []),
      ...(hasEditPermission && !rowData?.isDefaultLanguage
        ? [
            {
              label: "Autotranslate",
              onClick: () => handleTranslate(rowData),
            },
          ]
        : []),
      ...(hasDeletePermission && !rowData?.isDefaultLanguage
        ? [
            {
              label: "delete",
              onClick: () => handleOpenWarning(rowData),
            },
          ]
        : []),
    ];
  };

  const handleChangeState = useCallback(
    async (languageId: number, state: boolean) => {
      dispatch(setLoading(true));
      const { meta } = await dispatch(
        MakeCampaignTranslationVisible({ languageId, campaignId: params?.id })
      );
      if (meta.requestStatus !== ERequestStatus.FULFILLED) {
        dispatch(setLoading(false));
        return;
      }
      await refetchGrid();
      toast.success(
        `${t("survey")} ${t("is")} ${!state ? t("inactive") : t("active")}`
      );
      dispatch(setLoading(false));
    },
    [dispatch, params?.id, refetchGrid, t]
  );

  const translationColumns = useMemo(() => {
    return [
      ...columns,
      {
        label: "state",
        layout: (rowData: ICampaignTranslation) => {
          return (
            <Switch
              color="success"
              disabled={!hasEditPermission || isLoading}
              onChange={(e, checked) =>
                handleChangeState(rowData.languageID, checked)
              }
              checked={rowData.visibleToRespondents}
            />
          );
        },
      },
    ];
  }, [handleChangeState, hasEditPermission, isLoading]);

  const initialFetch = useCallback(async () => {
    await dispatch(setTableLoading(true));
    await dispatch(GetTranslationLanguages());
    await dispatch(GetAutoTranslateLanguageList());
    const { meta } = await dispatch(GetCampaignTranslationGrid(params?.id));
    if (meta.requestStatus === ERequestStatus.FULFILLED) {
      await dispatch(setTableLoading(false));
    } else {
      await dispatch(setTableLoading(false));
    }
  }, [dispatch, params?.id]);

  useEffect(() => {
    initialFetch();
  }, [initialFetch]);

  return (
    <Box p={4}>
      <Box display="flex" justifyContent="space-between">
        <Typography
          variant="h4"
          fontWeight={500}
          sx={{ color: "text.secondary" }}
        >
          {t("Translations")}
        </Typography>
        {hasCreatePermission && (
          <Button
            startIcon={
              <SvgIcon fontSize="small">
                <PlusIcon />
              </SvgIcon>
            }
            variant="contained"
            onClick={() => setDrawerOpen(true)}
          >
            {t("add")}
          </Button>
        )}
      </Box>
      <BasicTable
        toolbar={false}
        columns={translationColumns}
        data={data?.displayData || []}
        onChange={refetchGrid}
        getActions={getActions}
        enablePagination={false}
        sortable={false}
      />
      <RightDrawer
        open={isDrawerOpen}
        setOpen={setDrawerOpen}
        onClose={handleClose}
        title={t("add_campaign_translation")}
      >
        <AddCampaignTranslation
          campaignID={params?.id}
          onAddSuccess={onAddSuccess}
        />
      </RightDrawer>
      <SharedDialog
        open={isWarningOpen}
        setOpen={setWarningOpen}
        onSuccess={handleDelete}
        textConfig={{
          title: "warning",
          description: "delete_campaign_translation",
        }}
        minWidth="400px"
      />
      <SharedDialog
        maxWidth={"xl"}
        open={isQuestionsModalOpen}
        setOpen={setQuestionsModalOpen}
        hasActions={false}
        minWidth="400px"
        fullScreen
        textConfig={{
          title: "Questions",
        }}
      >
        <QuestionTranslation
          activeRow={activeRow}
          campaignID={params?.id}
          onAddSuccess={onUpdateSuccess}
        />
      </SharedDialog>
      <SharedDialog
        maxWidth={"xl"}
        open={isDistModalOpen}
        setOpen={setDistModalOpen}
        hasActions={false}
        minWidth="800px"
        textConfig={{
          title: "Distribution Texts",
        }}
      >
        <DistributionTextTranslation
          activeRow={activeRow}
          campaignID={params?.id}
          onAddSuccess={onAddEditDistributionSuccess}
        />
      </SharedDialog>
    </Box>
  );
};

export default CampaignTranslations;
